"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import { toast } from "sonner";

import { parseLocaleFromPathname } from "@/config/locale";
import {
	ADD_TO_CART_ATTEMPTED_EVENT,
	ADD_TO_CART_FAILED_EVENT,
	buildAddToCartAttemptProperties,
	buildAddToCartFailureProperties,
	normalizeAddToCartErrorCode,
	type AddToCartErrorCode,
	type AddToCartEventInput,
} from "@/lib/analytics/add-to-cart-events";
import { createBrowserUuid } from "@/lib/browser-uuid";

import { useCart } from "./cart-context";
import type { AddToCartRequest } from "./pending-cart-lines";

/**
 * The ONE add-to-cart runner. Every client surface (PDP buybox + sticky CTA,
 * calculator buy bridge, BAC cross-sell, homepage bundle, stack builder) and the
 * drawer's own retry button go through it, so the click → drawer → confirmation
 * choreography exists exactly once:
 *
 *   1. transient line into the CartContext   (drawer has something to show)
 *   2. open the drawer                       (before the request, not after)
 *   3. canonical attempt event               (once per cart_add_attempt_id)
 *   4. POST /api/cart/add
 *   5. success → confirm the line + `router.refresh()`; the transient line is retired
 *      by the authoritative props, never by a timer
 *      failure → mark the line failed (message + retry inside the drawer) + toast
 *
 * `openCart()`/`router.refresh()` deliberately sit OUTSIDE the request try/catch:
 * a throw from a success side effect must never be reported as an add-to-cart failure.
 */

/** posthog-js throws when read before init — analytics must never break the add. */
function readPosthogIds(): { distinctId: string; sessionId: string } {
	let distinctId = "";
	let sessionId = "";
	try {
		distinctId = posthog.get_distinct_id() ?? "";
	} catch {
		distinctId = "";
	}
	try {
		sessionId = posthog.get_session_id() ?? "";
	} catch {
		sessionId = "";
	}
	return { distinctId, sessionId };
}

function capturePosthog(event: string, properties: Record<string, unknown>): void {
	try {
		posthog.capture(event, properties);
	} catch {
		// Analytics must never prevent or mask a commerce mutation.
	}
}

function invokeSafely(callback: (() => void) | undefined): void {
	try {
		callback?.();
	} catch {
		// Surface-specific telemetry/callbacks are best effort after cart state wins.
	}
}

interface AddOutcome {
	ok: boolean;
	errorCode: AddToCartErrorCode;
	/** Saleor checkout the line landed on. Lets the drawer offer checkout on a FIRST add,
	 *  before any server props exist to carry the id. */
	checkoutId?: string;
	httpStatus?: number;
	/** Authoritative quantity of the variant after the add, when the API reports it. */
	confirmedQuantity?: number;
}

interface AddResponseBody {
	ok?: boolean;
	error?: unknown;
	checkoutId?: unknown;
	confirmedQuantity?: unknown;
}

async function postAdd(url: string, body: unknown): Promise<AddOutcome> {
	try {
		const response = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		});
		const payload = (await response.json().catch(() => null)) as AddResponseBody | null;

		if (!response.ok || !payload?.ok) {
			// The route answers 200 with `{ ok:false, error }` for business failures;
			// a non-2xx means infrastructure, so only then is the status informative.
			return {
				ok: false,
				errorCode: payload ? normalizeAddToCartErrorCode(payload.error) : "invalid-response",
				...(response.ok ? {} : { httpStatus: response.status }),
			};
		}

		return {
			ok: true,
			errorCode: "exception",
			...(typeof payload.checkoutId === "string" && payload.checkoutId
				? { checkoutId: payload.checkoutId }
				: {}),
			...(typeof payload.confirmedQuantity === "number" && Number.isFinite(payload.confirmedQuantity)
				? { confirmedQuantity: payload.confirmedQuantity }
				: {}),
		};
	} catch {
		return { ok: false, errorCode: "network" };
	}
}

interface AddToCartOptions {
	/** Retry: replaces that transient row in place instead of adding a second one. */
	replaceAttemptId?: string;
	/** Surface-specific analytics that must keep firing (e.g. the calculator events). */
	onAttempt?: (attemptId: string) => void;
	onSuccess?: (attemptId: string) => void;
	onFailure?: (attemptId: string, errorCode: AddToCartErrorCode) => void;
	/** Set when the surface shows its own error toast with its own copy. */
	suppressToast?: boolean;
}

export type AddToCartRunner = (request: AddToCartRequest, options?: AddToCartOptions) => Promise<boolean>;

export function useAddToCart(): AddToCartRunner {
	const router = useRouter();
	const {
		openCart,
		startPendingCartLine,
		replacePendingCartLine,
		settlePendingCartLine,
		isMutationBlocked,
		noteCheckoutId,
	} = useCart();
	const t = useTranslations("cart");
	const tPdp = useTranslations("pdpUx");
	/**
	 * Latest-ref so the toast's retry action never closes over a stale runner (and so
	 * the callback does not reference itself during its own declaration).
	 */
	const runnerRef = useRef<AddToCartRunner | null>(null);

	const run = useCallback<AddToCartRunner>(
		async (request, options = {}) => {
			if (isMutationBlocked?.()) {
				openCart("add_to_cart");
				toast.error(t("totalsUpdating"));
				return false;
			}
			const attemptId = createBrowserUuid();
			const { distinctId, sessionId } = readPosthogIds();
			const pathname = typeof window !== "undefined" ? window.location.pathname : "";
			const attemptStartedAtMs = Date.now();
			// No valid source/surface pair means the server drops `cart_add_attempt_id`
			// (see parseCartAttribution), so a client event could never be joined.
			const trackable = Boolean(request.internalSource && request.addSurface);

			const eventInput: AddToCartEventInput = {
				cartAddAttemptId: attemptId,
				productSlug: request.productSlug,
				productName: request.productName,
				variantId: request.variantId,
				variantName: request.variantName,
				quantity: request.quantity,
				channel: request.channel,
				locale: request.locale ?? parseLocaleFromPathname(pathname),
				internalSource: request.internalSource,
				internalSourceDetail: request.internalSourceDetail,
				addSurface: request.addSurface,
				experimentVariant: request.experimentVariant,
				pathname,
				sessionId,
			};

			if (options.replaceAttemptId) {
				// Double-click guard: the context resolves the row synchronously, so the
				// second click of a double-click finds it already retried and we abort
				// BEFORE the request — otherwise the visitor buys the line twice.
				if (replacePendingCartLine(options.replaceAttemptId, request, attemptId) === "blocked") {
					return false;
				}
			} else {
				startPendingCartLine(request, attemptId);
			}
			openCart("add_to_cart");

			invokeSafely(options.onAttempt ? () => options.onAttempt?.(attemptId) : undefined);
			if (trackable) {
				capturePosthog(ADD_TO_CART_ATTEMPTED_EVENT, buildAddToCartAttemptProperties(eventInput));
			}

			const outcome = await postAdd("/api/cart/add", {
				channel: request.channel,
				variantId: request.variantId,
				quantity: request.quantity,
				productSlug: request.productSlug,
				productName: request.productName,
				variantName: request.variantName,
				posthogDistinctId: distinctId,
				posthogSessionId: sessionId,
				pathname: pathname || undefined,
				attemptStartedAtMs,
				internalSource: request.internalSource,
				internalSourceDetail: request.internalSourceDetail,
				addSurface: request.addSurface,
				experimentVariant: request.experimentVariant,
				cartAddAttemptId: attemptId,
			});

			if (outcome.ok) {
				// Before settling the row, so the drawer's first render as "settling" already
				// has a checkout target. A scalar id, never money — it cannot produce a total.
				// Optional-call for the same reason as `isMutationBlocked?.()` above: surfaces
				// under test mount a partial context, and a missing analytics/convenience hook
				// must never turn a CONFIRMED add into a thrown rejection.
				if (outcome.checkoutId) noteCheckoutId?.(request.channel, outcome.checkoutId);
				settlePendingCartLine(attemptId, {
					status: "confirmed",
					confirmedQuantity: outcome.confirmedQuantity,
				});
				invokeSafely(options.onSuccess ? () => options.onSuccess?.(attemptId) : undefined);
				router.refresh();
				return true;
			}

			settlePendingCartLine(attemptId, { status: "failed", errorCode: outcome.errorCode });
			if (trackable) {
				capturePosthog(
					ADD_TO_CART_FAILED_EVENT,
					buildAddToCartFailureProperties({
						...eventInput,
						errorCode: outcome.errorCode,
						httpStatus: outcome.httpStatus,
						latencyMs: Date.now() - attemptStartedAtMs,
					}),
				);
			}
			invokeSafely(options.onFailure ? () => options.onFailure?.(attemptId, outcome.errorCode) : undefined);
			if (!options.suppressToast) {
				toast.error(tPdp("addToCartFailed"), {
					action: {
						label: t("retryAdd"),
						onClick: () => void runnerRef.current?.(request, { replaceAttemptId: attemptId }),
					},
				});
			}
			return false;
		},
		[
			isMutationBlocked,
			noteCheckoutId,
			openCart,
			replacePendingCartLine,
			router,
			settlePendingCartLine,
			startPendingCartLine,
			t,
			tPdp,
		],
	);

	useEffect(() => {
		runnerRef.current = run;
	});

	return run;
}

interface AddCartLinesOptions {
	/** Shared `cart_add_attempt_id` of the batch; generated when omitted. */
	cartAddAttemptId?: string;
	onSuccess?: () => void;
	onFailure?: () => void;
	/** The stack builder keeps its own copy, so the shared toast stays off by default. */
	suppressToast?: boolean;
}

export type AddCartLinesRunner = (
	requests: AddToCartRequest[],
	options?: AddCartLinesOptions,
) => Promise<boolean>;

/**
 * Batch sibling of `useAddToCart` for `/api/cart/add-lines` (stack builder): ONE
 * mutation for up to four lines so a partial retry cannot duplicate them. Every
 * request must share `channel` and attribution — the body carries them once.
 *
 * The canonical attempt/failure events stay off here: the batch route sends a single
 * `cart_add_attempt_id` for all lines and the surface emits its own funnel events.
 * A failed batch row is retried as a single add, which does emit the canonical pair.
 */
export function useAddCartLines(): AddCartLinesRunner {
	const router = useRouter();
	const { openCart, startPendingCartLine, settlePendingCartLine, isMutationBlocked, noteCheckoutId } =
		useCart();
	const t = useTranslations("pdpUx");

	return useCallback<AddCartLinesRunner>(
		async (requests, options = {}) => {
			if (requests.length === 0) return false;
			if (isMutationBlocked?.()) {
				openCart("add_to_cart");
				return false;
			}
			const first = requests[0]!;
			const { distinctId, sessionId } = readPosthogIds();
			const pathname = typeof window !== "undefined" ? window.location.pathname : "";
			// One row id per line (they share the analytics attempt id, not the row identity).
			const rows = requests.map((request) => ({ request, attemptId: createBrowserUuid() }));

			for (const row of rows) {
				startPendingCartLine(row.request, row.attemptId);
			}
			openCart("add_to_cart");

			const outcome = await postAdd("/api/cart/add-lines", {
				channel: first.channel,
				lines: requests.map((request) => ({
					variantId: request.variantId,
					quantity: request.quantity,
					productSlug: request.productSlug,
					productName: request.productName,
					variantName: request.variantName,
				})),
				posthogDistinctId: distinctId,
				posthogSessionId: sessionId,
				pathname,
				internalSource: first.internalSource,
				internalSourceDetail: first.internalSourceDetail,
				addSurface: first.addSurface,
				cartAddAttemptId: options.cartAddAttemptId ?? createBrowserUuid(),
			});

			if (outcome.ok) {
				// Same reason as the single-line path: the footer now renders for a live
				// transient row, so without a checkout id it would show a button that
				// silently swallows the click. `/api/cart/add-lines` already returns one.
				if (outcome.checkoutId) noteCheckoutId?.(first.channel, outcome.checkoutId);
				for (const row of rows) {
					settlePendingCartLine(row.attemptId, { status: "confirmed" });
				}
				invokeSafely(options.onSuccess);
				router.refresh();
				return true;
			}

			for (const row of rows) {
				settlePendingCartLine(row.attemptId, { status: "failed", errorCode: outcome.errorCode });
			}
			invokeSafely(options.onFailure);
			if (!options.suppressToast) toast.error(t("addToCartFailed"));
			return false;
		},
		[isMutationBlocked, noteCheckoutId, openCart, router, settlePendingCartLine, startPendingCartLine, t],
	);
}
